import type { Modifier } from './stats';
import type { Tag } from './tags';
import type { Perk } from './actions';
import { ITEMS, type WeaponClass } from './items';

export type HeroId = 'revenant' | 'pilgrim' | 'hexer' | 'druid' | 'warden' | 'bard' | 'leomorph' | 'mystic' | 'shaman' | 'goblins';

export interface HeroDef {
  id: HeroId;
  weapon: string;
  /** weapon classes this hero was trained for; anything else cannot be equipped */
  weapons: WeaponClass[];
  startSkill: string;
  /** the hero's skill pool; rank points unlock these into the kit */
  skills: string[];
  /** hero trait, applied permanently */
  trait: Perk & { id: string; color: string; glyph: string };
  baseMods: Modifier[];
  /** draft weight multipliers by tag */
  tagWeights: Partial<Record<Tag, number>>;
  color: string;
  accent: string;
}

export const HEROES: Record<HeroId, HeroDef> = {
  // ---------------------------------------------------------------- roots
  revenant: {
    id: 'revenant',
    weapon: 'claymore',
    weapons: ['heavy', 'light'],
    startSkill: 'wail',
    skills: ['wail', 'shield_bash', 'blood_rite', 'war_cry', 'void_rift', 'orbit_blades', 'mirror_shards', 'bone_shards'],
    trait: {
      id: 'cursed_resolve', color: '#8a7cff', glyph: '♆',
      mods: [{ stat: 'armor', add: 10 }, { stat: 'summonPower', inc: 0.15 }],
      flags: ['summonsInheritArmor'],
    },
    baseMods: [{ stat: 'maxHp', add: 40 }, { stat: 'moveSpeed', add: -10 }],
    tagWeights: { summon: 2.2, shield: 2, blunt: 1.6, slash: 1.6, void: 1.4, bleed: 1.3 },
    color: '#6f7fa8',
    accent: '#8a7cff',
  },
  pilgrim: {
    id: 'pilgrim',
    weapon: 'longbow',
    weapons: ['bow', 'light'],
    startSkill: 'light_volley',
    skills: ['light_volley', 'arrow_storm', 'holy_ward', 'chain_lightning', 'frost_nova', 'spirit_bolt', 'guardian_totem', 'orbit_blades'],
    trait: {
      id: 'mercy', color: '#ffe38a', glyph: '✚',
      mods: [{ stat: 'critChance', add: 0.05 }, { stat: 'healPower', inc: 0.2 }],
      hooks: [{ on: 'hit', chance: 0.2, cooldown: 0.5, action: { type: 'heal', target: 'lowest', radius: 320, power: 0.35 } }],
    },
    baseMods: [{ stat: 'moveSpeed', add: 15 }, { stat: 'maxHp', add: -10 }],
    tagWeights: { holy: 2.2, pierce: 2, crit: 1.8, heal: 1.8, projectile: 1.5, lightning: 1.2 },
    color: '#d8c8a0',
    accent: '#ffe38a',
  },
  hexer: {
    id: 'hexer',
    weapon: 'staff',
    weapons: ['staff', 'light'],
    startSkill: 'hex',
    skills: ['hex', 'fireball', 'frost_nova', 'chain_lightning', 'void_rift', 'ember_aura', 'frost_pulse', 'blight_cloud'],
    trait: {
      id: 'coven', color: '#c65cff', glyph: '☿',
      mods: [{ stat: 'auraPower', inc: 0.4 }, { stat: 'auraRadius', inc: 0.4 }, { stat: 'duration', inc: 0.2 }],
      flags: ['curseAmplify'],
    },
    baseMods: [{ stat: 'cooldown', inc: -0.08 }, { stat: 'maxHp', add: -5 }],
    tagWeights: { curse: 2.2, blight: 1.8, aura: 1.8, fire: 1.5, frost: 1.5, lightning: 1.5, void: 1.4 },
    color: '#5a3d7a',
    accent: '#c65cff',
  },
  // ---------------------------------------------------------------- first branches: two roots at level 10
  druid: {
    id: 'druid',
    weapon: 'sickle',
    weapons: ['light', 'staff'],
    startSkill: 'kindred_beast',
    skills: ['kindred_beast', 'walking_tree', 'vine', 'flytrap', 'thornroot', 'frost_nova', 'blight_cloud', 'guardian_totem'],
    trait: {
      id: 'wild_bond', color: '#8fe38a', glyph: '❦',
      mods: [{ stat: 'summonPower', inc: 0.3 }, { stat: 'healPower', inc: 0.2 }],
      hooks: [{ on: 'kill', chance: 0.3, cooldown: 1, action: { type: 'heal', target: 'allies', radius: 300, power: 0.3 } }],
    },
    baseMods: [{ stat: 'maxHp', add: 10 }],
    tagWeights: { summon: 2.6, heal: 2, blight: 1.4, frost: 1.2, aura: 1.2 },
    color: '#3a5a3a',
    accent: '#8fe38a',
  },
  warden: {
    id: 'warden',
    weapon: 'halberd',
    weapons: ['heavy', 'light'],
    startSkill: 'bulwark_oath',
    skills: ['bulwark_oath', 'shield_bash', 'war_cry', 'hex', 'frost_pulse', 'mirror_shards', 'holy_ward', 'orbit_blades'],
    trait: {
      id: 'sentinel', color: '#9fd3ff', glyph: '⛨',
      mods: [{ stat: 'armor', add: 15 }, { stat: 'thorns', add: 0.1 }],
      hooks: [{ on: 'damaged', chance: 0.35, cooldown: 2, action: { type: 'buff', target: 'enemies', at: 'self', radius: 140, status: [{ id: 'weak', duration: 2.5 }] } }],
    },
    baseMods: [{ stat: 'maxHp', add: 35 }, { stat: 'moveSpeed', add: -8 }],
    tagWeights: { shield: 2.4, curse: 1.8, blunt: 1.6, aura: 1.4, slash: 1.3 },
    color: '#5a6a80',
    accent: '#9fd3ff',
  },
  bard: {
    id: 'bard',
    weapon: 'lute',
    weapons: ['staff', 'bow'],
    startSkill: 'rallying_chord',
    skills: ['rallying_chord', 'light_volley', 'holy_ward', 'war_cry', 'arrow_storm', 'spirit_bolt', 'frost_pulse', 'chain_lightning'],
    trait: {
      id: 'encore', color: '#ffd166', glyph: '♫',
      mods: [{ stat: 'duration', inc: 0.3 }, { stat: 'auraRadius', inc: 0.3 }, { stat: 'healPower', inc: 0.15 }],
      hooks: [{ on: 'cast', tags: ['aura'], cooldown: 2, action: { type: 'buff', target: 'allies', radius: 260, status: [{ id: 'regen', duration: 4, stacks: 1 }] } }],
    },
    baseMods: [{ stat: 'moveSpeed', add: 10 }, { stat: 'maxHp', add: -5 }],
    tagWeights: { aura: 2.6, heal: 2, holy: 1.5, projectile: 1.4, crit: 1.2, movement: 1.2 },
    color: '#6a4a6a',
    accent: '#ffd166',
  },
  // ---------------------------------------------------------------- second branches: a branch and a root at level 15
  leomorph: {
    id: 'leomorph',
    weapon: 'claws',
    weapons: ['light', 'heavy'],
    startSkill: 'tiger_form',
    skills: ['tiger_form', 'blood_rite', 'shield_bash', 'orbit_blades', 'bone_shards', 'war_cry', 'ember_aura', 'frost_nova'],
    trait: {
      id: 'feral_vigor', color: '#ffb347', glyph: '♞',
      mods: [{ stat: 'maxHp', inc: 0.25 }, { stat: 'hpRegen', add: 1.5 }, { stat: 'lifesteal', add: 0.03 }],
    },
    baseMods: [{ stat: 'maxHp', add: 30 }, { stat: 'moveSpeed', add: 5 }, { stat: 'cooldown', inc: 0.05 }],
    tagWeights: { bleed: 2, slash: 1.9, movement: 1.8, heal: 1.2, blunt: 1.1 },
    color: '#8a5a2a',
    accent: '#ffb347',
  },
  mystic: {
    id: 'mystic',
    weapon: 'soul_orb',
    weapons: ['staff', 'light'],
    startSkill: 'spirit_pact',
    skills: ['spirit_pact', 'thornroot', 'void_rift', 'hex', 'blight_cloud', 'wail', 'spirit_bolt', 'ember_aura'],
    trait: {
      id: 'demonologist', color: '#ff6ad5', glyph: '⛧',
      mods: [{ stat: 'summonPower', inc: 0.2 }, { stat: 'duration', inc: 0.15 }],
      hooks: [{ on: 'cast', tags: ['summon'], cooldown: 1, action: { type: 'buff', target: 'enemies', at: 'self', radius: 220, status: [{ id: 'weak', duration: 3 }] } }],
    },
    baseMods: [{ stat: 'maxHp', add: -10 }, { stat: 'cooldown', inc: -0.05 }],
    tagWeights: { summon: 2.4, curse: 2, blight: 1.8, void: 1.6, aura: 1.2 },
    color: '#4a2a5a',
    accent: '#ff6ad5',
  },
  shaman: {
    id: 'shaman',
    weapon: 'totem_staff',
    weapons: ['staff', 'heavy'],
    startSkill: 'attunement',
    skills: ['attunement', 'fireball', 'frost_nova', 'chain_lightning', 'guardian_totem', 'ember_aura', 'frost_pulse', 'void_rift'],
    trait: {
      id: 'spirit_flow', color: '#ff8c42', glyph: '☄',
      mods: [{ stat: 'damage', inc: 0.1 }],
      hooks: [{ on: 'cast', tags: ['fire', 'frost', 'lightning'], cooldown: 1, action: { type: 'buff', target: 'self', status: [{ id: 'might', duration: 3 }] } }],
    },
    baseMods: [{ stat: 'maxHp', add: -5 }, { stat: 'projSpeed', inc: 0.1 }],
    tagWeights: { fire: 2, frost: 2, lightning: 2, projectile: 1.5, area: 1.3 },
    color: '#5a3a2a',
    accent: '#ff8c42',
  },
  // ---------------------------------------------------------------- crown: three second branches at max level
  goblins: {
    id: 'goblins',
    weapon: 'sparkgun',
    weapons: ['bow', 'staff'],
    startSkill: 'turret',
    skills: ['turret', 'snare_trap', 'arrow_storm', 'guardian_totem', 'bone_shards', 'fireball', 'mirror_shards', 'spirit_bolt'],
    trait: {
      id: 'tinkerers', color: '#a7ff5c', glyph: '⚙',
      mods: [{ stat: 'summonCount', add: 1 }, { stat: 'luck', add: 1 }, { stat: 'projSpeed', inc: 0.15 }],
      hooks: [{ on: 'kill', chance: 0.15, cooldown: 4, action: { type: 'summon', unit: 'gob_turret', count: 1, duration: 6, at: 'self' } }],
    },
    baseMods: [{ stat: 'maxHp', add: -15 }, { stat: 'moveSpeed', add: 8 }],
    tagWeights: { summon: 2, projectile: 2, void: 1.8, area: 1.6, movement: 1.2 },
    color: '#3a4a2a',
    accent: '#a7ff5c',
  },
};

export const HERO_IDS = Object.keys(HEROES) as HeroId[];

// ------------------------------------------------------------------ unlock tree

export interface HeroUnlock {
  hero: HeroId;
  level: number;
}

/** Heroes open when other heroes reach the listed levels; the mix of parents shapes the child. */
export const HERO_UNLOCKS: Record<HeroId, HeroUnlock[]> = {
  revenant: [],
  pilgrim: [],
  hexer: [],
  druid: [{ hero: 'revenant', level: 10 }, { hero: 'pilgrim', level: 10 }],
  warden: [{ hero: 'revenant', level: 10 }, { hero: 'hexer', level: 10 }],
  bard: [{ hero: 'pilgrim', level: 10 }, { hero: 'hexer', level: 10 }],
  leomorph: [{ hero: 'warden', level: 15 }, { hero: 'druid', level: 15 }],
  mystic: [{ hero: 'druid', level: 15 }, { hero: 'hexer', level: 15 }],
  shaman: [{ hero: 'bard', level: 15 }, { hero: 'hexer', level: 15 }],
  goblins: [{ hero: 'warden', level: 20 }, { hero: 'mystic', level: 20 }, { hero: 'shaman', level: 20 }],
};

/** Best level ever reached per hero on an account. */
export type HeroBest = Partial<Record<HeroId, number>>;

export function heroTier(id: HeroId): number {
  const needs = HERO_UNLOCKS[id];
  if (!needs.length) return 0;
  return 1 + Math.max(...needs.map((n) => heroTier(n.hero)));
}

/** Heroes grouped by tier, roots first. */
export const HERO_TIERS: HeroId[][] = (() => {
  const out: HeroId[][] = [];
  for (const id of HERO_IDS) {
    const t = heroTier(id);
    (out[t] ??= []).push(id);
  }
  return out;
})();

export function isHeroUnlocked(best: HeroBest, id: HeroId): boolean {
  return HERO_UNLOCKS[id].every((n) => (best[n.hero] ?? 0) >= n.level);
}

export function unlockedHeroes(best: HeroBest): HeroId[] {
  return HERO_IDS.filter((id) => isHeroUnlocked(best, id));
}

/** Records a reached level; returns true when the record improved. */
export function recordHeroBest(best: HeroBest, id: HeroId, level: number): boolean {
  if ((best[id] ?? 0) >= level) return false;
  best[id] = level;
  return true;
}

/** Whether this hero may wear the item: armour and charms fit anyone, weapons need the right class. */
export function canEquipItem(heroId: HeroId, defId: string): boolean {
  const def = ITEMS[defId];
  if (!def) return false;
  if (def.slot !== 'weapon') return true;
  return (HEROES[heroId]?.weapons ?? []).includes(def.wclass);
}
